import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import {
  ApplyCalendarLayoutAssignmentsOutDTO,
  CalendarLayoutAssignmentFilter,
  CalendarLayoutAssignmentRowOutDTO,
  CalendarLayoutAssignmentTargetInDTO,
  CalendarLayoutModelRowOutDTO,
  Pageable,
  PageResponseCalendarLayoutAssignmentRowOutDTO,
  CalendarLayoutStatusEnum,
} from '@api/ringil-api.v2';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, MassActionConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Checkbox } from 'primereact/checkbox';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ASSIGN, REMOVE } from './assignmentActions';
import { useAdminCompanyOptions } from './useAdminCompanyOptions';
import { PICKER_PANEL_CLASS, truncatedValueTemplate, wrappingItemTemplate } from './pickerProps';

/**
 * A table row. The backend returns rows for scopes that have *no* assignment too (so an admin can
 * see who is still on the default and assign to them), and those rows carry no `id` — which the
 * data table uses as its selection identity. A synthetic key by scope keeps every row distinct.
 */
type AssignmentRow = CalendarLayoutAssignmentRowOutDTO & { id: string; assignmentId?: string };

const rowKey = (row: CalendarLayoutAssignmentRowOutDTO): string =>
  row.id ?? `unassigned:${row.companyId ?? ''}:${row.locationId ?? ''}:${row.calendarLayoutScopeType}`;

interface CalendarLayoutAssignmentsTableProps {
  admin: boolean;
  refreshToken?: number;
  /** Called after assignments change, so the models list (its "applied to" counts) refreshes too. */
  onChanged?: () => void;
}

export const CalendarLayoutAssignmentsTable: React.FC<CalendarLayoutAssignmentsTableProps> = ({ admin, refreshToken, onChanged }) => {
  const { tr } = useTranslationLgs();
  const { listAssignments, applyAssignments, listCalendarLayoutModels } = useContext(ApiContext);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: AssignmentRow[]; clear: () => void }>(null);
  const { options: companyOptions } = useAdminCompanyOptions(admin, 'all');
  const [models, setModels] = useState<CalendarLayoutModelRowOutDTO[]>([]);
  const [assignDialog, setAssignDialog] = useState<{ targets: AssignmentRow[] } | null>(null);
  const [chosenModelId, setChosenModelId] = useState<string | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [results, setResults] = useState<ApplyCalendarLayoutAssignmentsOutDTO | null>(null);
  const [busy, setBusy] = useState(false);

  // Only published models can be assigned — assigning an unpublished one would render nothing.
  useEffect(() => {
    listCalendarLayoutModels({ pageSize: 200, pageNumber: 1 }, { calendarLayoutStatus: CalendarLayoutStatusEnum.ACTIVE }, response => setModels(response.data.filter(model => model.published)));
  }, [refreshToken]);

  const reload = () => {
    tableRef.current?.reloadData();
    onChanged?.();
  };

  const loadData = (filter: CalendarLayoutAssignmentFilter, pageable: Pageable, onSuccess: (paged: PagedData<AssignmentRow>) => void) => {
    listAssignments(pageable, filter, (response: PageResponseCalendarLayoutAssignmentRowOutDTO) => {
      onSuccess({
        data: response.data.map(row => ({ ...row, assignmentId: row.id, id: rowKey(row) })),
        total: response.total,
        page: response.page,
        size: response.size,
      });
    });
  };

  const apply = (targets: CalendarLayoutAssignmentTargetInDTO[], modelId?: string) => {
    setBusy(true);
    applyAssignments(
      { modelId, replaceExisting, targets },
      data => {
        setBusy(false);
        setResults(data);
        setAssignDialog(null);
        tableRef.current?.clear();
        reload();
      },
      undefined,
      { onError: () => setBusy(false) },
    );
  };

  const openAssign = (targets: AssignmentRow[]) => {
    if (targets.length === 0) return;
    setChosenModelId(null);
    setAssignDialog({ targets });
  };

  const confirmAssign = () => {
    if (!assignDialog || !chosenModelId) return;
    apply(
      assignDialog.targets.map(row => ({ calendarLayoutAction: ASSIGN, companyId: row.companyId, locationId: row.locationId, modelId: chosenModelId })),
      chosenModelId,
    );
  };

  const removeOverrides = (rows: AssignmentRow[]) =>
    apply(rows.filter(row => !!row.assignmentId).map(row => ({ calendarLayoutAction: REMOVE, companyId: row.companyId, locationId: row.locationId })));

  const scopeLabel = (scope: CalendarLayoutAssignmentRowOutDTO['calendarLayoutScopeType']) => {
    if (scope === 'LOCATION') return tr('CalendarLayout.scope.location', 'Location');
    if (scope === 'COMPANY') return tr('CalendarLayout.scope.company', 'Company');
    return tr('CalendarLayout.scope.systemDefault', 'System default');
  };

  const columnsConfig: ColumnConfig<AssignmentRow>[] = [
    {
      id: 'company',
      header: 'CalendarLayout.column.company',
      headerDefault: 'Company',
      width: '22%',
      sortable: false,
      order: 1,
      verticalAlign: 'middle',
      render: row => <span className="font-bold">{row.companyName ?? tr('CalendarLayout.scopeSystem', 'System default')}</span>,
    },
    {
      id: 'location',
      header: 'CalendarLayout.column.location',
      headerDefault: 'Location',
      width: '20%',
      sortable: false,
      order: 2,
      verticalAlign: 'middle',
      render: row => (
        <span className="text-sm">
          {row.locationName ?? (row.calendarLayoutScopeType === 'COMPANY' ? tr('CalendarLayout.wholeCompany', 'Whole company') : '—')}
        </span>
      ),
    },
    {
      // The scope the backend resolved this row at, as its own column rather than inferred from
      // which of the two columns above happens to be filled in.
      id: 'scope',
      header: 'CalendarLayout.column.scope',
      headerDefault: 'Scope',
      width: '12%',
      sortable: false,
      order: 3,
      verticalAlign: 'middle',
      render: row => <span className="text-xs text-content-secondary">{scopeLabel(row.calendarLayoutScopeType)}</span>,
    },
    {
      id: 'model',
      header: 'CalendarLayout.column.model',
      headerDefault: 'Renders',
      width: '18%',
      sortable: false,
      order: 4,
      verticalAlign: 'middle',
      render: row => <span>{row.modelName}</span>,
    },
    {
      id: 'status',
      header: 'CalendarLayout.column.modelStatus',
      headerDefault: 'Model status',
      width: '8%',
      sortable: false,
      order: 5,
      verticalAlign: 'middle',
      render: row => (
        <span className={row.calendarLayoutModelStatus === 'ARCHIVED' ? 'text-xs text-content-tertiary' : 'text-xs'}>
          {row.calendarLayoutModelStatus === 'ARCHIVED' ? tr('CalendarLayout.archived', 'Archived') : tr('CalendarLayout.active', 'Active')}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'CalendarLayout.column.actions',
      headerDefault: '',
      width: '20%',
      sortable: false,
      order: 6,
      verticalAlign: 'middle',
      render: row => (
        <div className="flex flex-row flex-nowrap items-center gap-1 whitespace-nowrap">
          <RingilButton label={tr('CalendarLayout.assign', 'Assign model')} mode="table" onClick={() => openAssign([row])} />
          {row.assignmentId && (
            <RingilButton label={tr('CalendarLayout.removeOverride', 'Remove override')} mode="table" onClick={() => removeOverrides([row])} />
          )}
        </div>
      ),
    },
  ];

  const massActionConfig: MassActionConfig<AssignmentRow>[] = [
    {
      label: tr('CalendarLayout.assignSelected', 'Assign model to selected'),
      onClick: () => openAssign(tableRef.current?.selectedObjects ?? []),
    },
    {
      label: tr('CalendarLayout.removeSelected', 'Remove overrides'),
      onClick: () => removeOverrides(tableRef.current?.selectedObjects ?? []),
      disabled: selected => selected.every(row => !row.assignmentId),
    },
  ];

  const filtersConf: FilterConfig[] = [
    // Company, then location, then model — narrowing from the widest scope to the narrowest.
    // Every company, not only the ones that already have an assignment: the common admin question
    // is "what does company X render", and X is usually one that has never been configured.
    ...(admin
      ? [
          {
            filterType: FilterType.Dropdown,
            placeholder: tr('CalendarLayout.filter.company', 'Company'),
            value: ['companyId'],
            options: companyOptions,
          } as FilterConfig,
        ]
      : []),
    { filterType: FilterType.InputText, placeholder: tr('CalendarLayout.filter.location', 'Location'), value: ['q'] },
    { filterType: FilterType.InputText, placeholder: tr('CalendarLayout.filter.name', 'Model name'), value: ['name'] },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('CalendarLayout.filter.scopeType', 'Scope'),
      value: ['calendarLayoutScopeType'],
      options: [
        { label: tr('CalendarLayout.scope.location', 'Location'), value: 'LOCATION', active: true },
        { label: tr('CalendarLayout.scope.company', 'Company'), value: 'COMPANY', active: true },
        { label: tr('CalendarLayout.scope.systemDefault', 'System default'), value: 'SYSTEM_DEFAULT', active: true },
      ],
    },
  ];

  const selectedModel = models.find(model => model.id === chosenModelId);

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-content-secondary">
        {admin
          ? tr('CalendarLayout.assignmentsHintAdmin', 'Every layout assigned to a company or location. Anything not listed here renders the system default.')
          : tr('CalendarLayout.assignmentsHintCompany', 'Layouts assigned to your company and its locations. Anything not listed here renders the default.')}
      </div>

      <RingilDataTable
        key={refreshToken}
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        loadDataFromApi={loadData}
        filterName={admin ? 'admin_calendar_layout_assignments' : 'calendar_layout_assignments'}
        filtersConf={filtersConf}
        massActionConfig={massActionConfig as MassActionConfig<unknown>[]}
      />

      {/* Assign */}
      <RingilDialog
        visible={assignDialog !== null}
        onHide={() => setAssignDialog(null)}
        heading={tr('CalendarLayout.assignHeading', 'Assign a layout')}
        buttons={
          <>
            <RingilButton label={tr('CalendarLayout.assign', 'Assign model')} mode="filled" disabled={busy || !chosenModelId} onClick={confirmAssign} />
            <RingilButton label={tr('CalendarLayout.cancel', 'Cancel')} mode="outlined" onClick={() => setAssignDialog(null)} />
          </>
        }
      >
        <div className="flex flex-col gap-3 w-[520px]">
          <div className="text-sm">
            {tr('CalendarLayout.assignTargets', 'Applies to {{count}} target(s):', { count: assignDialog?.targets.length ?? 0 })}
          </div>
          <div className="max-h-40 overflow-y-auto text-xs text-content-secondary flex flex-col gap-0.5">
            {(assignDialog?.targets ?? []).map(target => (
              <div key={target.id}>{[target.companyName, target.locationName].filter(Boolean).join(' — ')}</div>
            ))}
          </div>
          <Field label={tr('CalendarLayout.model', 'Model')} required={!chosenModelId}>
            <Dropdown
              value={chosenModelId}
              options={models.map(model => ({ label: model.systemWide ? `${model.name} (${tr('CalendarLayout.systemWide', 'System-wide')})` : model.name, value: model.id }))}
              onChange={e => setChosenModelId(e.value)}
              filter
              placeholder={tr('CalendarLayout.choosePublishedModel', 'Choose a published model')}
              className="w-full min-w-0"
              panelClassName={PICKER_PANEL_CLASS}
              itemTemplate={wrappingItemTemplate}
              valueTemplate={truncatedValueTemplate(tr('CalendarLayout.choosePublishedModel', 'Choose a published model'))}
            />
          </Field>
          {selectedModel && selectedModel.hasDraft && (
            <div className="text-xs text-content-secondary">
              {tr('CalendarLayout.assignDraftNote', 'This model has an unpublished draft — the published layout is what will render.')}
            </div>
          )}
          <label className="flex flex-row items-center gap-2 cursor-pointer text-sm">
            <Checkbox checked={replaceExisting} onChange={e => setReplaceExisting(!!e.checked)} />
            <span>{tr('CalendarLayout.replaceExisting', 'Replace layouts these targets already have')}</span>
          </label>
        </div>
      </RingilDialog>

      {/* Per-target outcome — a partial failure has to be legible, not guessed at. */}
      <RingilDialog
        visible={results !== null}
        onHide={() => setResults(null)}
        heading={tr('CalendarLayout.assignResults', 'Result')}
        buttons={<RingilButton label={tr('CalendarLayout.close', 'Close')} mode="filled" onClick={() => setResults(null)} />}
      >
        <div className="flex flex-col gap-1 w-[520px] max-h-[60vh] overflow-y-auto text-xs">
          {(results?.results ?? []).map((result, index) => (
            <div key={`${result.companyId}:${result.locationId ?? ''}:${index}`} className="flex flex-row justify-between gap-3 border-b border-0 border-solid border-content-quaternary py-1">
              <span>{[result.companyId, result.locationId].filter(Boolean).join(' — ')}</span>
              <span className={result.calendarLayoutOutcome === 'SKIPPED' ? 'text-content-tertiary' : 'text-content-primary'}>
                {result.calendarLayoutOutcome}
                {result.reason ? ` — ${result.reason}` : ''}
              </span>
            </div>
          ))}
        </div>
      </RingilDialog>
    </div>
  );
};
